<template>
  <header class="sticky top-0 z-50 border-b border-ink-100 bg-white/80 backdrop-blur-xl 
           dark:bg-ink-950/80 dark:border-ink-800 transition-colors duration-300"
    :class="scrolled ? 'shadow-[0_8px_30px_-18px_rgba(0,0,0,.28)] dark:shadow-none' : ''" aria-label="Site header">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">

      <router-link :to="`/${langue}`"
        class="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 rounded-lg">
        <img src="@/assets/img/logo/ar.svg" alt="Anjah R."
          class="h-16 md:h-20 w-auto dark:invert dark:brightness-0 transition-all duration-300" />
        <span class="sr-only">Accueil</span>
      </router-link>

      <nav class="hidden md:flex items-center gap-1 relative" aria-label="Primary">
        <template v-for="(nav, i) in navLinks" :key="i">

          <a v-if="isExternal(nav.action)" :href="nav.action" target="_blank" rel="noopener" class="relative px-3 py-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300
                   text-ink-700 hover:text-brand-600 
                   dark:text-ink-200 dark:hover:text-brand-400">
            <span>{{ nav.label || 'Lien' }}</span>
            <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px] opacity-80"></i>

            <span
              class="pointer-events-none absolute left-3 right-3 -bottom-[2px] h-[2px] rounded-full bg-gradient-to-r from-brand-300 to-brand-600 opacity-0 transition-opacity duration-300"
              :class="hoverUnderlineClass(false)" />
          </a>

          <router-link v-else :to="localized(nav.action)" class="relative px-6 py-2 rounded-lg font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300
                   text-ink-700 hover:text-brand-600 
                   dark:text-ink-200 dark:hover:text-brand-400"
            :class="isActive(localized(nav.action)) ? 'text-brand-600 dark:text-brand-400' : ''">
            <span>{{ nav.label || 'Lien' }}</span>

            <span
              class="pointer-events-none absolute left-3 right-3 -bottom-[2px] h-[2px] rounded-full bg-gradient-to-r from-brand-300 to-brand-600 transition-all duration-300 ease-in-out"
              :class="isActive(localized(nav.action)) ? 'opacity-100' : 'opacity-0'" />
          </router-link>
        </template>
      </nav>

      <div class="hidden md:flex items-center gap-3">

        <ThemeToggle />

        <div class="relative">
          <button @click="toggleLangDropdown" class="flex items-center gap-2 px-3 py-2 rounded-full border transition
                   bg-white border-ink-200 text-ink-800 hover:bg-ink-50 
                   dark:bg-ink-900 dark:border-ink-700 dark:text-ink-200 dark:hover:bg-ink-800" aria-haspopup="listbox"
            :aria-expanded="isLangDropdownOpen ? 'true' : 'false'"
            :aria-label="langue === 'fr' ? 'Langue : Français' : 'Language: English'">
            <span class="text-base leading-none">
              {{ langue === 'fr' ? '🇨🇦' : '🇺🇸' }}
            </span> <span class="ml-1 text-sm font-medium">{{ langue === 'fr' ? 'FR' : 'EN' }}</span>
            <i class="fas fa-chevron-down text-xs opacity-70"></i>
          </button>

          <ul v-if="isLangDropdownOpen" class="absolute right-0 mt-2 w-40 rounded-xl shadow-[0_16px_40px_-20px_rgba(0,0,0,.35)] overflow-hidden
                   bg-white border border-ink-200 
                   dark:bg-ink-900 dark:border-ink-700" role="listbox">
            <li>
              <button class="w-full text-left px-3 py-2 flex items-center gap-2
                             hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-ink-800" @click="changeLanguage('fr')">
                <span class="w-5 h-3 rounded-sm shadow-sm"> 🇨🇦 </span>
                <span>Français</span>
              </button>
            </li>
            <li>
              <button class="w-full text-left px-3 py-2 flex items-center gap-2
                             hover:bg-ink-50 dark:text-ink-200 dark:hover:bg-ink-800" @click="changeLanguage('en')">
                <span class=" w-5 h-3 rounded-sm shadow-sm"> 🇺🇸 </span>
                <span>English</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <button class="md:hidden rounded-lg p-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300
               text-ink-800 hover:bg-ink-100 
               dark:text-ink-100 dark:hover:bg-ink-800" @click="isMobileMenuOpen = !isMobileMenuOpen"
        :aria-expanded="isMobileMenuOpen ? 'true' : 'false'" aria-controls="mobile-menu" aria-label="Menu">
        <i :class="isMobileMenuOpen ? 'fas fa-xmark' : 'fas fa-bars'"></i>
      </button>
    </div>

    <transition name="slide">
      <div v-if="isMobileMenuOpen" id="mobile-menu" class="md:hidden border-t transition-colors
                  border-ink-100 bg-white 
                  dark:border-ink-800 dark:bg-ink-950" @keydown.escape="isMobileMenuOpen = false">

        <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-2" aria-label="Mobile">
          <template v-for="(nav, i) in navLinks" :key="'m' + i">
            <a v-if="isExternal(nav.action)" :href="nav.action" target="_blank" rel="noopener"
              @click="isMobileMenuOpen = false" class="relative block px-3 py-3 rounded-lg font-medium transition-colors duration-200
                     text-ink-800 hover:bg-brand-50 hover:text-brand-600
                     dark:text-ink-200 dark:hover:bg-ink-900 dark:hover:text-brand-400">
              <span>{{ nav.label || 'Lien' }}</span>
              <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px] opacity-80"></i>
            </a>

            <router-link v-else :to="localized(nav.action)" @click="isMobileMenuOpen = false" class="relative block px-3 py-3 rounded-lg font-medium transition-colors duration-200
                     text-ink-800 hover:bg-brand-50 hover:text-brand-600
                     dark:text-ink-200 dark:hover:bg-ink-900 dark:hover:text-brand-400"
              :class="isActive(localized(nav.action)) ? 'text-brand-600 dark:text-brand-400 bg-brand-50/50 dark:bg-ink-900' : ''">
              <span>{{ nav.label || 'Lien' }}</span>
              <span v-if="isActive(localized(nav.action))"
                class="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-brand-600 dark:bg-brand-400" />
            </router-link>
          </template>

          <div class="pt-4 mt-2 border-t border-ink-100 dark:border-ink-800 flex flex-col gap-4">

            <div class="flex items-center justify-between px-1">
              <span class="text-sm font-semibold text-ink-700 dark:text-ink-400">
                {{ langue === 'fr' ? 'Apparence' : 'Appearance' }}
              </span>
              <ThemeToggle />
            </div>

            <div>
              <label class="block mb-2 text-sm font-semibold text-ink-700 dark:text-ink-400 px-1">
                {{ langue === 'fr' ? 'Langue' : 'Language' }}
              </label>
              <div class="grid grid-cols-2 gap-3">
                <button class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition
                         border-ink-200 hover:bg-ink-50 
                         dark:border-ink-700 dark:text-ink-200 dark:hover:bg-ink-800"
                  :class="langue === 'fr' ? 'ring-1 ring-brand-300 bg-brand-50/30 dark:bg-ink-800' : ''"
                  @click="changeLanguage('fr')">
                  <span class="fi fi-ca w-5 h-3 rounded-sm shadow-sm"></span>
                  <span>Français</span>
                </button>
                <button class="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition
                         border-ink-200 hover:bg-ink-50 
                         dark:border-ink-700 dark:text-ink-200 dark:hover:bg-ink-800"
                  :class="langue === 'en' ? 'ring-1 ring-brand-300 bg-brand-50/30 dark:bg-ink-800' : ''"
                  @click="changeLanguage('en')">
                  <span class="fi fi-us w-5 h-3 rounded-sm shadow-sm"></span>
                  <span>English</span>
                </button>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </transition>
  </header>
</template>

<script>
import { mapGetters } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";
import ThemeToggle from "@/components/ui/ThemeToggle.vue"; // 👈 IMPORT IMPORTANT

export default {
  name: "HighHeader",
  components: { ThemeToggle }, // 👈 ENREGISTREMENT
  data() {
    return {
      navLinks: [],
      isMobileMenuOpen: false,
      isLangDropdownOpen: false,
      scrolled: false,
      _unwatchRoute: null,
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),
    langue: {
      get() { return this.getLangue; },
      set(v) { this.$store.commit("setLangue", v); },
    },
  },
  methods: {
    async fetchPages(lang) {
      const l = String(lang || this.$route.params.langue || this.langue || "fr").trim();
      if (!l || l === "undefined" || l === "null") return;

      try {
        const { data } = await http.get("GlobalContent", {
          params: { type: "navHeader", langue: l }
        });
        this.navLinks = Array.isArray(data?.boutons) ? data.boutons : [];
      } catch (e) {
        if (e.response && e.response.status === 400) return;
        console.error("[HighHeader] Erreur chargement nav:", e);
      }
    },
    isExternal(path) { return /^https?:\/\//i.test(String(path || "")); },
    ensureLocalePrefix(path, lang) {
      if (!path) return `/${lang}`;
      if (this.isExternal(path)) return path;
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    localized(path) { return this.ensureLocalePrefix(path, this.langue); },
    setLanguage(lang) {
      this.$store.commit("setLangue", lang);
      localStorage.setItem("locale", lang);
    },
    // --- Logique de résolution de slug (conservée) ---
    parseServiceDetailPath(path) {
      const m = /^\/(fr|en)\/services\/([^\/?#]+)/i.exec(String(path || ""));
      return m ? { lang: m[1], slug: decodeURIComponent(m[2]) } : null;
    },
    parseServiceTagPath(path) {
      const m = /^\/(fr|en)\/services\/tag\/([^\/?#]+)/i.exec(String(path || ""));
      return m ? { lang: m[1], tag: decodeURIComponent(m[2]) } : null;
    },
    async resolveDetailSlugForLang(targetLang) {
      const info = this.parseServiceDetailPath(this.$route.path);
      if (!info) return null;
      try {
        const srcId = await fetchPageIdByCode("home", info.lang);
        const dstId = await fetchPageIdByCode("home", targetLang);
        const { data } = await http.get("service-detail/resolve-slug", {
          params: { source_page_id: srcId, target_page_id: dstId, slug: info.slug },
        });
        return data?.found && data?.slug ? String(data.slug) : null;
      } catch (e) { return null; }
    },
    async resolveTagForLang(targetLang) {
      const info = this.parseServiceTagPath(this.$route.path);
      if (!info) return null;
      try {
        const srcId = await fetchPageIdByCode("home", info.lang);
        const dstId = await fetchPageIdByCode("home", targetLang);
        const { data } = await http.get("services/resolve-tag", {
          params: { source_page_id: srcId, target_page_id: dstId, tag: info.tag },
        });
        return data?.found && data?.tag ? String(data.tag) : info.tag;
      } catch { return info.tag; }
    },
    mapRouteNameForLang(curName, targetLang) {
      if (targetLang === "fr" && curName === "about-en") return "about-fr";
      if (targetLang === "en" && curName === "about-fr") return "about-en";
      return curName || undefined;
    },
    async changeLanguage(lang) {
      this.isLangDropdownOpen = false;
      this.setLanguage(lang);
      const { name, params, query, hash, path } = this.$route;
      const safeQuery = { ...query };

      // Logique switch langue
      const isDetail = this.parseServiceDetailPath(path);
      if (isDetail && name === "service-detail") {
        const resolved = await this.resolveDetailSlugForLang(lang);
        this.$router.push({ name: "service-detail", params: { ...params, langue: lang, slug: resolved || isDetail.slug }, query: safeQuery, hash }).catch(() => { });
        this.fetchPages(lang); return;
      }
      const isTag = this.parseServiceTagPath(path);
      if (isTag && name === "services-by-tag") {
        const targetTag = await this.resolveTagForLang(lang);
        this.$router.push({ name: "services-by-tag", params: { ...params, langue: lang, tag: targetTag }, query: safeQuery, hash }).catch(() => { });
        this.fetchPages(lang); return;
      }
      const targetName = this.mapRouteNameForLang(name, lang);
      if (targetName) {
        this.$router.push({ name: targetName, params: { ...params, langue: lang }, query: safeQuery, hash }).catch(() => { });
      } else {
        const full = this.$route.fullPath || "/";
        const next = /^\/(fr|en)\b/.test(full) ? full.replace(/^\/(fr|en)/, `/${lang}`) : `/${lang}`;
        if (next !== full) this.$router.push(next).catch(() => { });
      }
      this.fetchPages(lang);
    },
    toggleLangDropdown() { this.isLangDropdownOpen = !this.isLangDropdownOpen; },
    handleClickOutside(e) { if (this.$el && !this.$el.contains(e.target)) { this.isLangDropdownOpen = false; } },
    isActive(targetPath) { const cur = this.$route.path || "/"; return cur.startsWith(targetPath.replace(/\/+$/, "")); },
    onScroll() { this.scrolled = window.scrollY > 4; },
    hoverUnderlineClass() { return ""; } // Placeholder si besoin
  },
  mounted() {
    const langFromRoute = this.$route.params.langue || this.langue || "fr";
    if (langFromRoute !== this.langue) this.setLanguage(langFromRoute);
    this.fetchPages(this.langue);
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this._unwatchRoute = this.$watch(() => this.$route.fullPath, () => {
      this.isMobileMenuOpen = false;
      this.isLangDropdownOpen = false;
    });
    this.$watch(() => this.$route.params.langue, (newLang) => {
      if (newLang && newLang !== this.langue) { this.setLanguage(newLang); this.fetchPages(newLang); }
    });
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.onScroll);
    if (this._unwatchRoute) this._unwatchRoute();
  }
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateY(-6px);
  opacity: 0;
}
</style>